import { SupabaseClient } from "@supabase/supabase-js";
import { v4 as uuidv4 } from "uuid";
import { createCache } from "@/lib/cache";

const userProfileCache = createCache<any>();
const isAdminCache = createCache<boolean>();

/**
 * Fetch a user's profile information from the database.
 *
 * @param client - Supabase client
 * @param userId - ID of the user
 * @returns Profile data or null on error
 */
export async function getUserProfile(client: SupabaseClient, userId: string) {
  const { data, error } = await client
    .from("user_profiles")
    .select("*")
    .eq("id", userId)
    .single();

  if (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }

  return data;
}

/**
 * Create a user profile if one does not already exist.
 *
 * @param client - Supabase client
 * @param user - Auth user information
 * @returns Newly created or existing profile record
 */
export async function createUserProfileIfNotExists(
  client: SupabaseClient,
  user: { id: string; email?: string }
) {
  const { data: existingProfile, error: checkError } = await client
    .from("user_profiles")
    .select("id")
    .eq("id", user.id)
    .single();

  if (checkError && checkError.code !== "PGRST116") {
    console.error("Error checking user profile:", checkError);
    return null;
  }

  if (existingProfile) {
    return existingProfile;
  }

  const username = user.email
    ? user.email.split("@")[0].toLowerCase().replace(/[^a-z0-9-]/g, "-")
    : "user";

  const friendlyId = `${username}-${uuidv4().slice(0, 8)}`;

  const { data, error } = await client
    .from("user_profiles")
    .insert({
      id: user.id,
      friendly_id: friendlyId,
      display_name: user.email?.split("@")[0] || "New User",
      role: "user",
      is_approved: true,
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Error creating user profile: ${error.message}`);
  }

  return data;
}

/**
 * Determine if the given user has an admin role.
 *
 * @param client - Supabase client
 * @param userId - ID of the user
 * @returns True when the user is an admin
 */
export async function isUserAdmin(
  client: SupabaseClient,
  userId: string
): Promise<boolean> {
  const profile = await getUserProfile(client, userId);
  return profile?.role === "admin";
}

/**
 * Remove cached profile information for a specific user.
 */
export function invalidateUserCache(userId: string) {
  userProfileCache.delete(userId);
  isAdminCache.delete(userId);
}

/**
 * Clear all cached profile and admin status entries.
 */
export function clearUserCaches() {
  userProfileCache.clear();
  isAdminCache.clear();
}

/**
 * Mark a user profile as approved by an administrator.
 *
 * @param client - Supabase client
 * @param userId - Profile identifier
 * @returns Updated profile record
 */
export async function approveUser(
  client: SupabaseClient,
  userId: string
): Promise<any> {
  const { data, error } = await client
    .from("user_profiles")
    .update({
      is_approved: true,
      updated_at: new Date().toISOString(),
    })
    .eq("id", userId)
    .select("*")
    .single();

  if (error) {
    throw new Error(`Error approving user: ${error.message}`);
  }

  return data;
}
